import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { DestroyRef, effect, inject, Injectable, NgZone, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { catchError, map, of } from 'rxjs';
import { getApiUrl } from '../../utilities';
import { SnackbarService } from './toast/snackbar.service';

@Injectable({ providedIn: 'root' })
export class InternetStatusService {
  private readonly _isOnline = signal(true);
  private readonly reloadDelaySec: number = 10;
  private readonly lostMessageDurationSec: number = 10;
  public readonly isOnline = this._isOnline.asReadonly();
  public readonly snackbar = inject(SnackbarService);

  private readonly http = inject(HttpClient);
  private readonly destroyRef = inject(DestroyRef);

  private wasOffline = false;
  private reloadTimeout: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private translate: TranslateService,
    private readonly ngZone: NgZone,
  ) {}

  public start(): void {
    window.addEventListener('online', this.onBrowserOnline);
    window.addEventListener('offline', this.onBrowserOffline);

    this.destroyRef.onDestroy(() => {
      window.removeEventListener('online', this.onBrowserOnline);
      window.removeEventListener('offline', this.onBrowserOffline);
    });

    if (!navigator.onLine) {
      this.confirmOffline();
    }

    effect(() => {
      const online = this._isOnline();

      if (!online) {
        this.wasOffline = true;
        this.clearRestartTimeout();
        this.snackbar.open(this.translate.instant('general.internetStatus.lost'), this.lostMessageDurationSec * 1000);
      } else if (this.wasOffline) {
        const ref = this.snackbar.open(
          this.translate.instant('general.internetStatus.restored', { seconds: this.reloadDelaySec }),
          0,
          this.translate.instant('general.internetStatus.cancelReload'),
        );

        ref.onAction().subscribe(() => {
          this.clearRestartTimeout();
        });

        this.ngZone.runOutsideAngular(() => {
          this.reloadTimeout = setTimeout(() => {
            location.reload();
          }, this.reloadDelaySec * 1000);
        });
      }
    });
  }

  private readonly onBrowserOnline = (): void => {
    this._isOnline.set(true);
  };

  private readonly onBrowserOffline = (): void => {
    this.confirmOffline();
  };

  private confirmOffline(): void {
    this.http
      .head(getApiUrl(), {
        headers: { 'Skip-Error-Message-And-Redirect': 'true' },
        // ngsw-bypass is required: without it the service worker handles the request and its
        // safeFetch() turns a network failure into a synthetic 504, so status is never 0.
        params: { 'ngsw-bypass': 'true', probe: Date.now() },
      })
      .pipe(
        map(() => true),
        catchError((error: HttpErrorResponse) => of(error.status !== 0)),
      )
      .subscribe((isReachable: boolean) => {
        if (!isReachable) {
          this._isOnline.set(false);
        }
      });
  }

  private clearRestartTimeout(): void {
    if (this.reloadTimeout) {
      clearTimeout(this.reloadTimeout);
      this.reloadTimeout = null;
    }
  }
}
